import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';
import PageHeader from '../components/ui/PageHeader';
import { hasPermission, PERMISSIONS, handleAPIError } from '../authUtils';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const StatusBadge = ({ status }) => {
  const statusColorMap = {
    draft: { bg: COLORS.STATUS_DRAFT_BG, text: COLORS.STATUS_DRAFT_TEXT },
    active: { bg: COLORS.STATUS_ACTIVE_BG, text: COLORS.STATUS_ACTIVE_TEXT },
    funded: { bg: COLORS.STATUS_FUNDED_BG, text: COLORS.STATUS_FUNDED_TEXT },
    declined: { bg: COLORS.STATUS_DECLINED_BG, text: COLORS.STATUS_DECLINED_TEXT },
    closed: { bg: COLORS.STATUS_CLOSED_BG, text: COLORS.STATUS_CLOSED_TEXT },
  };
  const color = statusColorMap[status] || statusColorMap.draft;
  return (
    <span style={{
      display: 'inline-block',
      padding: `${SPACING.XS} ${SPACING.MD}`,
      borderRadius: RADIUS.FULL,
      fontSize: TYPOGRAPHY.BODY_SM.fontSize,
      fontWeight: '600',
      backgroundColor: color.bg,
      color: color.text,
    }}>
      {status}
    </span>
  );
};

export default function Deals() {
  const navigate = useNavigate();
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('list', '/dashboard');
  const [deals, setDeals] = useState([]);
  const [merchants, setMerchants] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    fetchDeals();
    fetchMerchants();
  }, []);

  // Set up unified header
  useEffect(() => {
    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: 'Advances',
      subtitle: null,
      actions: [
        hasPermission(PERMISSIONS.DEAL_CREATE) && (
          <Link key="add" to="/deals/new" style={{
            padding: `${SPACING.SM} ${SPACING.LG}`,
            backgroundColor: COLORS.PRIMARY_DARK,
            color: COLORS.TEXT_WHITE,
            border: 'none',
            borderRadius: RADIUS.DEFAULT,
            fontSize: TYPOGRAPHY.BODY.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            textDecoration: 'none',
            display: 'inline-block',
            transition: 'all 0.2s ease',
          }}>
            + New Advance
          </Link>
        ),
      ].filter(Boolean),
    });

    // Cleanup: reset header when unmounting
    return () => resetHeaderConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderConfig, resetHeaderConfig]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/deals`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const dealsData = response.data.deals || response.data;
      setDeals(dealsData);
      setError(null);
    } catch (err) {
      const processedError = handleAPIError(err);
      setError(err);
      console.error('Failed to load deals:', processedError);
    } finally {
      setLoading(false);
    }
  };

  const fetchMerchants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/merchants`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const merchantMap = {};
      response.data.forEach(m => {
        merchantMap[m.id] = m.legal_name;
      });
      setMerchants(merchantMap);
    } catch (err) {
      console.error('Failed to load merchants', err);
    }
  };

  const filteredDeals = deals.filter(deal => {
    const statusMatch = statusFilter === 'all' || deal.status === statusFilter;
    const searchMatch = searchFilter === '' ||
      (deal.visual_deal_id?.toLowerCase().includes(searchFilter.toLowerCase())) ||
      (merchants[deal.merchant_id]?.toLowerCase().includes(searchFilter.toLowerCase()));
    return statusMatch && searchMatch;
  });

  const sortedDeals = [...filteredDeals].sort((a, b) =>
    new Date(b.created_at) - new Date(a.created_at)
  );

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: SPACING.XXXL, color: COLORS.TEXT_LIGHT }}>
          <p style={TYPOGRAPHY.BODY}>Loading advances...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        {error && (
        <div style={{
          padding: SPACING.LG,
          backgroundColor: '#fee2e2',
          border: `1px solid #fca5a5`,
          borderRadius: RADIUS.DEFAULT,
          color: COLORS.ERROR,
          marginBottom: SPACING.XL,
        }}>
          {error.message || 'Failed to load advances'}
        </div>
      )}

      {/* Page Header */}
      <PageHeader
        title="Advances"
        subtitle="Manage your advance opportunities and track progress"
        primaryAction={
          hasPermission(PERMISSIONS.DEAL_CREATE) ? (
            <Link
              to="/deals/new"
              style={styles.primaryButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(77, 219, 148, 0.45)';
                e.currentTarget.style.backgroundColor = '#0a2c25';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = SHADOWS.DEFAULT;
                e.currentTarget.style.backgroundColor = COLORS.PRIMARY_DARK;
              }}
            >
              + New Advance
            </Link>
          ) : null
        }
      />

      {/* Filters */}
      <div style={styles.filterBar}>
        <input
          type="text"
          placeholder="Search by Advance ID or Merchant..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="funded">Funded</option>
          <option value="declined">Declined</option>
          <option value="closed">Closed</option>
        </select>
        <span style={{...TYPOGRAPHY.BODY_SM, color: COLORS.TEXT_LIGHT, whiteSpace: 'nowrap'}}>
          {sortedDeals.length} advance{sortedDeals.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Content */}
      {sortedDeals.length === 0 ? (
        <div style={styles.card}>
          <div style={{ textAlign: 'center', padding: SPACING.XXXL }}>
            <h3 style={{...TYPOGRAPHY.H3, color: COLORS.TEXT_LIGHT, margin: 0, marginBottom: SPACING.MD}}>
              No advances found
            </h3>
            {statusFilter === 'all' ? (
              <>
                <p style={{...TYPOGRAPHY.BODY, color: COLORS.TEXT_LIGHT, margin: 0, marginBottom: SPACING.LG}}>
                  Get started by creating your first advance.
                </p>
                <Link to="/deals/new" style={styles.primaryButton}>
                  Create Advance
                </Link>
              </>
            ) : (
              <p style={{...TYPOGRAPHY.BODY, color: COLORS.TEXT_LIGHT, margin: 0}}>
                Try adjusting the filter.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div style={styles.card}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.headerRow}>
                <th style={styles.headerCell}>Advance ID</th>
                <th style={styles.headerCell}>Merchant</th>
                <th style={styles.headerCell}>Status</th>
                <th style={styles.headerCell}>State</th>
                <th style={styles.headerCell}>Amount</th>
                <th style={styles.headerCell}>Created</th>
              </tr>
            </thead>
            <tbody>
              {sortedDeals.map((deal, idx) => (
                <tr
                  key={deal.id}
                  style={{
                    ...styles.tableRow,
                    borderBottom: idx < sortedDeals.length - 1 ? `1px solid ${COLORS.BORDER_LIGHT}` : 'none',
                  }}
                  onClick={() => navigate(`/deals/${deal.id}`)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = COLORS.BG_SURFACE_SECONDARY}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={{...styles.tableCell, fontWeight: '600'}}>{deal.visual_deal_id}</td>
                  <td style={styles.tableCell}>{merchants[deal.merchant_id] || 'Unknown'}</td>
                  <td style={styles.tableCell}><StatusBadge status={deal.status} /></td>
                  <td style={styles.tableCell}>{deal.working_data?.state || '—'}</td>
                  <td style={styles.tableCell}>
                    ${parseFloat(deal.working_data?.deal_terms?.purchase_price || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </td>
                  <td style={styles.tableCell}>
                    {new Date(deal.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
}

const styles = {
  pageWrapper: {
    backgroundColor: COLORS.BG_PAGE_SURFACE,
    minHeight: '100vh',
  },

  container: {
    padding: `${SPACING.XXL} ${SPACING.XL}`,
    maxWidth: '1400px',
    margin: '0 auto',
  },

  primaryButton: {
    padding: `${SPACING.MD} ${SPACING.XL}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    textDecoration: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY.fontSize,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'inline-block',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(77, 219, 148, 0.3)',
    letterSpacing: '0.3px',
  },

  filterBar: {
    display: 'flex',
    gap: SPACING.LG,
    alignItems: 'center',
    marginBottom: SPACING.XL,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    padding: `${SPACING.MD} ${SPACING.LG}`,
    borderRadius: RADIUS.DEFAULT,
  },

  searchInput: {
    flex: 1,
    maxWidth: '400px',
    padding: `${SPACING.SM} ${SPACING.MD}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    outline: 'none',
  },

  filterSelect: {
    padding: `${SPACING.SM} ${SPACING.MD}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    color: COLORS.TEXT_PRIMARY,
    outline: 'none',
  },

  card: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    boxShadow: SHADOWS.DEFAULT,
    overflow: 'hidden',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
  },

  headerRow: {
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
  },

  headerCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    textAlign: 'left',
    fontWeight: '700',
    fontSize: TYPOGRAPHY.BODY_SM.fontSize,
    color: COLORS.TEXT_PRIMARY,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  tableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },

  tableCell: {
    padding: `${SPACING.MD} ${SPACING.LG}`,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    color: COLORS.TEXT_PRIMARY,
  },
};
